import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Rocket, Server, Plus, Trash2, Edit2, Check, X, Clock, CheckCircle, XCircle, AlertTriangle, RotateCcw, Eye, EyeOff, Upload, FileText, Settings2, History } from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  ready: 'bg-blue-50 text-blue-700 border-blue-300',
  deployed: 'bg-green-50 text-green-700 border-green-300',
  rolled_back: 'bg-orange-50 text-orange-700 border-orange-300',
  failed: 'bg-red-50 text-red-700 border-red-300',
};

const statusLabels = {
  draft: 'Brouillon',
  ready: 'Pret',
  deployed: 'Deploye',
  rolled_back: 'Rollback',
  failed: 'Echoue',
};

const DeployTab = () => {
  const [versions, setVersions] = useState([]);
  const [servers, setServers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [showServerDialog, setShowServerDialog] = useState(false);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [showLogDetail, setShowLogDetail] = useState(null);
  const [editingVersion, setEditingVersion] = useState(null);
  const [editingServer, setEditingServer] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showQuickVersion, setShowQuickVersion] = useState(false);
  const [quickForm, setQuickForm] = useState({ description: '', changes: '' });

  // Forms
  const [versionForm, setVersionForm] = useState({ version: '', description: '', changes: '', env_vars: '' });
  const [serverForm, setServerForm] = useState({ name: '', protocol: 'ftp', host: '', port: 21, username: '', password: '', remote_path: '/' });
  const [deployForm, setDeployForm] = useState({ version_id: '', server_config_id: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [vRes, sRes, lRes] = await Promise.all([
        axios.get(`${API}/admin/deploy/versions`, { withCredentials: true }),
        axios.get(`${API}/admin/deploy/servers`, { withCredentials: true }),
        axios.get(`${API}/admin/deploy/logs`, { withCredentials: true })
      ]);
      setVersions(vRes.data);
      setServers(sRes.data);
      setLogs(lRes.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- Version CRUD ---
  const saveVersion = async (e) => {
    e.preventDefault();
    const changes = versionForm.changes.split('\n').filter(c => c.trim());
    let env_vars = {};
    try {
      if (versionForm.env_vars.trim()) {
        versionForm.env_vars.split('\n').filter(l => l.includes('=')).forEach(l => {
          const [k, ...v] = l.split('=');
          env_vars[k.trim()] = v.join('=').trim();
        });
      }
    } catch { /* ignore */ }

    try {
      if (editingVersion) {
        await axios.patch(`${API}/admin/deploy/versions/${editingVersion.version_id}`, {
          description: versionForm.description, changes, env_vars
        }, { withCredentials: true });
        toast.success('Version mise a jour');
      } else {
        await axios.post(`${API}/admin/deploy/versions`, {
          version: versionForm.version, description: versionForm.description, changes, env_vars
        }, { withCredentials: true });
        toast.success('Version creee');
      }
      setShowVersionDialog(false);
      setEditingVersion(null);
      setVersionForm({ version: '', description: '', changes: '', env_vars: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteVersion = async (id) => {
    if (!window.confirm('Supprimer cette version ?')) return;
    try {
      await axios.delete(`${API}/admin/deploy/versions/${id}`, { withCredentials: true });
      toast.success('Version supprimee');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const updateVersionStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/deploy/versions/${id}`, { status }, { withCredentials: true });
      toast.success(`Statut: ${statusLabels[status]}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const rollbackVersion = async (id) => {
    if (!window.confirm('Confirmer le rollback de cette version ?')) return;
    try {
      await axios.post(`${API}/admin/deploy/versions/${id}/rollback`, {}, { withCredentials: true });
      toast.success('Version marquee comme rolled back');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const quickCreateVersion = async (e) => {
    e.preventDefault();
    try {
      const changes = quickForm.changes.split('\n').filter(c => c.trim());
      await axios.post(`${API}/admin/deploy/versions/auto-create`, {
        description: quickForm.description,
        changes
      }, { withCredentials: true });
      toast.success('Version creee');
      setShowQuickVersion(false);
      setQuickForm({ description: '', changes: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // --- Server CRUD ---
  const saveServer = async (e) => {
    e.preventDefault();
    try {
      if (editingServer) {
        const update = { ...serverForm };
        if (update.password === '***') delete update.password;
        await axios.patch(`${API}/admin/deploy/servers/${editingServer.server_id}`, update, { withCredentials: true });
        toast.success('Serveur mis a jour');
      } else {
        await axios.post(`${API}/admin/deploy/servers`, serverForm, { withCredentials: true });
        toast.success('Serveur ajoute');
      }
      setShowServerDialog(false);
      setEditingServer(null);
      setServerForm({ name: '', protocol: 'ftp', host: '', port: 21, username: '', password: '', remote_path: '/' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const deleteServer = async (id) => {
    if (!window.confirm('Supprimer ce serveur ?')) return;
    try {
      await axios.delete(`${API}/admin/deploy/servers/${id}`, { withCredentials: true });
      toast.success('Serveur supprime');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // --- Deploy ---
  const triggerDeploy = async (e) => {
    e.preventDefault();
    if (!deployForm.version_id || !deployForm.server_config_id) return;
    setDeploying(true);
    try {
      const res = await axios.post(`${API}/admin/deploy/trigger`, deployForm, { withCredentials: true });
      if (res.data.status === 'success') {
        toast.success('Deploiement reussi !');
      } else {
        toast.error('Deploiement echoue');
      }
      setShowDeployDialog(false);
      setDeployForm({ version_id: '', server_config_id: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de deploiement');
    } finally { setDeploying(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return <div className="text-center py-12 text-charcoal/50">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="deploy-tab">
      <Tabs defaultValue="versions" className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList className="bg-white border-2 border-charcoal rounded-full p-1">
            <TabsTrigger value="versions" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal px-4" data-testid="deploy-tab-versions">
              <FileText className="w-4 h-4 mr-2" /> Versions
            </TabsTrigger>
            <TabsTrigger value="servers" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal px-4" data-testid="deploy-tab-servers">
              <Server className="w-4 h-4 mr-2" /> Serveurs
            </TabsTrigger>
            <TabsTrigger value="logs" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal px-4" data-testid="deploy-tab-logs">
              <History className="w-4 h-4 mr-2" /> Historique
            </TabsTrigger>
          </TabsList>

          <button
            onClick={() => setShowDeployDialog(true)}
            disabled={versions.filter(v => v.status === 'ready' || v.status === 'draft').length === 0 || servers.length === 0}
            className="btn-primary flex items-center gap-2 disabled:opacity-40"
            data-testid="deploy-btn"
          >
            <Rocket className="w-4 h-4" /> Deployer
          </button>
        </div>

        {/* === VERSIONS === */}
        <TabsContent value="versions">
          <div className="card-brutal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-charcoal font-heading">Versions</h2>
              <button
                onClick={() => { setEditingVersion(null); setVersionForm({ version: '', description: '', changes: '', env_vars: '' }); setShowVersionDialog(true); }}
                className="btn-outline text-sm flex items-center gap-2"
                data-testid="add-version-btn"
              >
                <Plus className="w-4 h-4" /> Nouvelle version
              </button>
              <button
                onClick={() => { setQuickForm({ description: '', changes: '' }); setShowQuickVersion(true); }}
                className="btn-primary text-sm flex items-center gap-2"
                data-testid="quick-version-btn"
              >
                <Rocket className="w-4 h-4" /> Version rapide
              </button>
            </div>

            {versions.length === 0 ? (
              <p className="text-charcoal/50 text-center py-8">Aucune version creee</p>
            ) : (
              <div className="space-y-3">
                {versions.map((v) => (
                  <div key={v.version_id} className="p-4 rounded-xl border-2 border-charcoal/10 hover:border-charcoal/30 transition-all" data-testid={`version-${v.version_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-heading font-bold text-lg text-charcoal">v{v.version}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${statusColors[v.status] || statusColors.draft}`}>
                            {statusLabels[v.status] || v.status}
                          </span>
                        </div>
                        <p className="text-sm text-charcoal/70">{v.description}</p>
                        {v.changes && v.changes.length > 0 && (
                          <ul className="mt-2 space-y-0.5">
                            {v.changes.slice(0, 3).map((c, i) => (
                              <li key={i} className="text-xs text-charcoal/50 flex items-center gap-1">
                                <Check className="w-3 h-3 text-green-500" /> {c}
                              </li>
                            ))}
                            {v.changes.length > 3 && <li className="text-xs text-charcoal/40">+{v.changes.length - 3} autres...</li>}
                          </ul>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-charcoal/40">
                          <span>{formatDate(v.created_at)}</span>
                          {v.deployed_at && <span>Deploye: {formatDate(v.deployed_at)}</span>}
                          {v.deploy_server && <span>Sur: {v.deploy_server}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {v.status === 'draft' && (
                          <button onClick={() => updateVersionStatus(v.version_id, 'ready')} className="p-2 rounded-full border border-blue-300 text-blue-600 hover:bg-blue-50" title="Marquer pret">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {v.status === 'deployed' && (
                          <button onClick={() => rollbackVersion(v.version_id)} className="p-2 rounded-full border border-orange-300 text-orange-600 hover:bg-orange-50" title="Rollback">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditingVersion(v);
                            setVersionForm({
                              version: v.version,
                              description: v.description,
                              changes: (v.changes || []).join('\n'),
                              env_vars: Object.entries(v.env_vars || {}).map(([k, val]) => `${k}=${val}`).join('\n')
                            });
                            setShowVersionDialog(true);
                          }}
                          className="p-2 rounded-full border border-charcoal/20 text-charcoal/60 hover:border-charcoal"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {v.status !== 'deployed' && (
                          <button onClick={() => deleteVersion(v.version_id)} className="p-2 rounded-full border border-charcoal/20 text-danger hover:border-danger" title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* === SERVERS === */}
        <TabsContent value="servers">
          <div className="card-brutal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-charcoal font-heading">Serveurs de deploiement</h2>
              <button
                onClick={() => { setEditingServer(null); setServerForm({ name: '', protocol: 'ftp', host: '', port: 21, username: '', password: '', remote_path: '/' }); setShowServerDialog(true); }}
                className="btn-outline text-sm flex items-center gap-2"
                data-testid="add-server-btn"
              >
                <Plus className="w-4 h-4" /> Ajouter un serveur
              </button>
            </div>

            {servers.length === 0 ? (
              <p className="text-charcoal/50 text-center py-8">Aucun serveur configure</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {servers.map((s) => (
                  <div key={s.server_id} className="p-4 rounded-xl border-2 border-charcoal/10 hover:border-charcoal/30 transition-all" data-testid={`server-${s.server_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Server className="w-4 h-4 text-charcoal/50" />
                          <span className="font-bold text-charcoal">{s.name}</span>
                        </div>
                        <p className="text-xs text-charcoal/50">{s.protocol.toUpperCase()} - {s.host}:{s.port}</p>
                        <p className="text-xs text-charcoal/40 mt-1">Chemin: {s.remote_path}</p>
                        <p className="text-xs text-charcoal/40">User: {s.username}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditingServer(s);
                            setServerForm({ ...s });
                            setShowServerDialog(true);
                          }}
                          className="p-2 rounded-full border border-charcoal/20 hover:border-charcoal"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button onClick={() => deleteServer(s.server_id)} className="p-2 rounded-full border border-charcoal/20 text-danger hover:border-danger">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* === LOGS === */}
        <TabsContent value="logs">
          <div className="card-brutal">
            <h2 className="text-xl font-bold text-charcoal font-heading mb-6">Historique des deploiements</h2>

            {logs.length === 0 ? (
              <p className="text-charcoal/50 text-center py-8">Aucun deploiement effectue</p>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.log_id} className="p-4 rounded-xl border-2 border-charcoal/10" data-testid={`log-${log.log_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {log.status === 'success' ? <CheckCircle className="w-5 h-5 text-green-500" /> :
                         log.status === 'failed' ? <XCircle className="w-5 h-5 text-danger" /> :
                         <Clock className="w-5 h-5 text-charcoal/50 animate-spin" />}
                        <div>
                          <p className="font-bold text-sm text-charcoal">v{log.version} → {log.server_name}</p>
                          <p className="text-xs text-charcoal/50">Par {log.triggered_by_name} - {formatDate(log.started_at)}</p>
                        </div>
                      </div>
                      <button onClick={() => setShowLogDetail(showLogDetail === log.log_id ? null : log.log_id)} className="text-xs text-primary font-bold hover:underline">
                        {showLogDetail === log.log_id ? 'Masquer' : 'Details'}
                      </button>
                    </div>
                    {showLogDetail === log.log_id && log.steps && (
                      <div className="mt-3 ml-8 space-y-1">
                        {log.steps.map((step, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            {step.status === 'success' ? <Check className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /> :
                             step.status === 'failed' ? <X className="w-3 h-3 text-danger mt-0.5 shrink-0" /> :
                             <Clock className="w-3 h-3 text-charcoal/30 mt-0.5 shrink-0" />}
                            <div>
                              <span className="text-charcoal/70">{step.step}</span>
                              {step.detail && <span className="text-charcoal/40 ml-1">- {step.detail}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* === VERSION DIALOG === */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-heading">
              {editingVersion ? 'Modifier la version' : 'Nouvelle version'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveVersion} className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Numero de version *</label>
              <input
                type="text"
                value={versionForm.version}
                onChange={(e) => setVersionForm({ ...versionForm, version: e.target.value })}
                className="input-brutal w-full"
                placeholder="1.0.0"
                disabled={!!editingVersion}
                required
                data-testid="version-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Description *</label>
              <input
                type="text"
                value={versionForm.description}
                onChange={(e) => setVersionForm({ ...versionForm, description: e.target.value })}
                className="input-brutal w-full"
                placeholder="Mise a jour des notifications..."
                required
                data-testid="version-desc-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Changements (1 par ligne)</label>
              <textarea
                value={versionForm.changes}
                onChange={(e) => setVersionForm({ ...versionForm, changes: e.target.value })}
                className="input-brutal w-full h-24 resize-none"
                placeholder="Ajout notifications push&#10;Fix messagerie&#10;Nouveau design carte"
                data-testid="version-changes-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Variables d'environnement (CLE=valeur)</label>
              <textarea
                value={versionForm.env_vars}
                onChange={(e) => setVersionForm({ ...versionForm, env_vars: e.target.value })}
                className="input-brutal w-full h-20 resize-none font-mono text-xs"
                placeholder="RESEND_API_KEY=re_xxx&#10;STRIPE_KEY=sk_xxx"
                data-testid="version-env-input"
              />
            </div>
            <button type="submit" className="w-full btn-primary" data-testid="save-version-btn">
              {editingVersion ? 'Mettre a jour' : 'Creer la version'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* === SERVER DIALOG === */}
      <Dialog open={showServerDialog} onOpenChange={setShowServerDialog}>
        <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-heading">
              {editingServer ? 'Modifier le serveur' : 'Ajouter un serveur'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveServer} className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Nom *</label>
              <input
                type="text"
                value={serverForm.name}
                onChange={(e) => setServerForm({ ...serverForm, name: e.target.value })}
                className="input-brutal w-full"
                placeholder="Production OVH"
                required
                data-testid="server-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Protocole</label>
                <select
                  value={serverForm.protocol}
                  onChange={(e) => setServerForm({ ...serverForm, protocol: e.target.value, port: e.target.value === 'sftp' ? 22 : 21 })}
                  className="input-brutal w-full"
                  data-testid="server-protocol-select"
                >
                  <option value="ftp">FTP</option>
                  <option value="sftp">SFTP</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Port</label>
                <input
                  type="number"
                  value={serverForm.port}
                  onChange={(e) => setServerForm({ ...serverForm, port: parseInt(e.target.value) })}
                  className="input-brutal w-full"
                  data-testid="server-port-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Host *</label>
              <input
                type="text"
                value={serverForm.host}
                onChange={(e) => setServerForm({ ...serverForm, host: e.target.value })}
                className="input-brutal w-full"
                placeholder="ftp.hebergeur.com"
                required
                data-testid="server-host-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Utilisateur *</label>
                <input
                  type="text"
                  value={serverForm.username}
                  onChange={(e) => setServerForm({ ...serverForm, username: e.target.value })}
                  className="input-brutal w-full"
                  required
                  data-testid="server-user-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Mot de passe *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={serverForm.password}
                    onChange={(e) => setServerForm({ ...serverForm, password: e.target.value })}
                    className="input-brutal w-full pr-10"
                    required={!editingServer}
                    data-testid="server-pass-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                    {showPassword ? <EyeOff className="w-4 h-4 text-charcoal/40" /> : <Eye className="w-4 h-4 text-charcoal/40" />}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Chemin distant *</label>
              <input
                type="text"
                value={serverForm.remote_path}
                onChange={(e) => setServerForm({ ...serverForm, remote_path: e.target.value })}
                className="input-brutal w-full"
                placeholder="/www/tagora.fr/"
                required
                data-testid="server-path-input"
              />
            </div>
            <button type="submit" className="w-full btn-primary" data-testid="save-server-btn">
              {editingServer ? 'Mettre a jour' : 'Ajouter le serveur'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* === DEPLOY DIALOG === */}
      <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-heading flex items-center gap-2">
              <Rocket className="w-5 h-5" /> Lancer un deploiement
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={triggerDeploy} className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Version a deployer *</label>
              <select
                value={deployForm.version_id}
                onChange={(e) => setDeployForm({ ...deployForm, version_id: e.target.value })}
                className="input-brutal w-full"
                required
                data-testid="deploy-version-select"
              >
                <option value="">Choisir une version...</option>
                {versions.filter(v => v.status === 'ready' || v.status === 'draft').map(v => (
                  <option key={v.version_id} value={v.version_id}>v{v.version} - {v.description}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Serveur de destination *</label>
              <select
                value={deployForm.server_config_id}
                onChange={(e) => setDeployForm({ ...deployForm, server_config_id: e.target.value })}
                className="input-brutal w-full"
                required
                data-testid="deploy-server-select"
              >
                <option value="">Choisir un serveur...</option>
                {servers.map(s => (
                  <option key={s.server_id} value={s.server_id}>{s.name} ({s.protocol.toUpperCase()} - {s.host})</option>
                ))}
              </select>
            </div>

            {deployForm.version_id && (
              <div className="p-3 bg-surface-alt rounded-xl border border-charcoal/10">
                <p className="text-xs text-charcoal/60">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Le deploiement va envoyer les fichiers de configuration et les variables d'environnement sur le serveur selectionne.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={deploying || !deployForm.version_id || !deployForm.server_config_id}
              className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="trigger-deploy-btn"
            >
              <Rocket className="w-4 h-4" />
              {deploying ? 'Deploiement en cours...' : 'Deployer maintenant'}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* === QUICK VERSION DIALOG === */}
      <Dialog open={showQuickVersion} onOpenChange={setShowQuickVersion}>
        <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-heading flex items-center gap-2">
              <Rocket className="w-5 h-5" /> Nouvelle version rapide
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-charcoal/50">Le numero de version sera auto-incremente</p>
          <form onSubmit={quickCreateVersion} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Resume de la version *</label>
              <input
                type="text"
                value={quickForm.description}
                onChange={(e) => setQuickForm({ ...quickForm, description: e.target.value })}
                className="input-brutal w-full"
                placeholder="Ex: Ajout notifications push + fix messagerie"
                required
                data-testid="quick-version-desc"
              />
            </div>
            <div>
              <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-1.5 block">Nouvelles features / corrections (1 par ligne)</label>
              <textarea
                value={quickForm.changes}
                onChange={(e) => setQuickForm({ ...quickForm, changes: e.target.value })}
                className="input-brutal w-full h-32 resize-none"
                placeholder={"Notifications push integrees\nFix messagerie tour par tour\nCentre de notifications dans le header\nSysteme de deploiement backoffice"}
                data-testid="quick-version-changes"
              />
            </div>
            <button type="submit" className="w-full btn-primary" data-testid="quick-version-submit">
              Creer la version
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeployTab;
